import React, { useState, useEffect } from 'react';
import { ChevronDown, Calendar, Download, Building2, CheckCircle, Users, TrendingUp, FileText } from 'lucide-react';
import { payrollAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import * as XLSX from 'xlsx';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const MonthlyPayrollRegistry = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Year options (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEstablishment(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch establishments on component mount
  useEffect(() => {
    if (lockedEstId) { setLoading(false); return; } // ClientAdmin: use locked est
    const fetchEstablishments = async () => {
      try {
        setLoading(true);
        const response = await establishmentAPI.getAll();
        const estabs = response.establishments || [];
        setEstablishments(estabs);
        if (estabs.length > 0 && !selectedEstablishment) {
          setSelectedEstablishment(estabs[0].id.toString());
        }
      } catch (error) {
        console.error('Error fetching establishments:', error);
        alert('Error loading establishments: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEstablishments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch employees when establishment changes
  const handleEstablishmentChange = async (e) => {
    const value = e.target.value;
    setSelectedEstablishment(value);
    setSelectedEmployee('');
    setEmployees([]);
    setPayrollData([]);
    setSummary(null);
  };

  // Fetch payroll data when filters change
  useEffect(() => {
    const fetchPayrollData = async () => {
      if (!selectedEstablishment) return;

      try {
        setDataLoading(true);

        const params = {
          establishment_id: selectedEstablishment,
          month: selectedMonth,
          year: selectedYear
        };

        if (selectedEmployee) {
          params.employee_id = selectedEmployee;
        }

        console.log('📊 Fetching payroll data:', params);
        const response = await payrollAPI.getMonthly(params);

        console.log('📥 Received payroll data:', response);
        setPayrollData(response.payroll || []);
        setSummary(response.summary || null);

      } catch (error) {
        console.error('Error fetching payroll data:', error);
        // Don't show alert if it's just no data
        if (error.message && !error.message.includes('404')) {
          alert('Error loading payroll data: ' + error.message);
        }
        setPayrollData([]);
        setSummary(null);
      } finally {
        setDataLoading(false);
      }
    };

    fetchPayrollData();
  }, [selectedEstablishment, selectedEmployee, selectedMonth, selectedYear]);

  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    if (!num || isNaN(num)) return '0';
    return parseFloat(num).toFixed(2);
  };

  const getMonthLabel = () => {
    const month = months.find(m => m.value === parseInt(selectedMonth));
    return month ? `${month.label} ${selectedYear}` : `${selectedMonth}/${selectedYear}`;
  };

  const handleGeneratePayroll = async () => {
    if (!selectedEstablishment) {
      alert('Please select an organization first');
      return;
    }

    if (!window.confirm(`Process payroll for ${getMonthLabel()}?\n\nThis will calculate and save payroll data for all employees in the selected organization.`)) {
      return;
    }

    try {
      setIsGenerating(true);

      const payload = {
        establishment_id: parseInt(selectedEstablishment),
        employee_id: selectedEmployee || null,
        month: selectedMonth,
        year: selectedYear
      };

      console.log('🔄 Generating payroll:', payload);
      const response = await payrollAPI.calculateMonthly(payload);

      console.log('✅ Payroll processed:', response);

      // Show result with errors if any
      let message = `${response.message}\n\nProcessed: ${response.processed}/${response.total} employees`;

      if (response.errors && response.errors.length > 0) {
        message += `\n\n⚠️ Errors:\n${response.errors.join('\n')}`;
        alert(`⚠️ Partial Success\n\n${message}`);
      } else if (response.processed === 0) {
        alert(`❌ Failed\n\n${message}\n\nNo employees were processed. Please check the console for details.`);
        return; // Don't refresh if nothing was processed
      } else {
        notifySavedAndReload(`✅ Payroll Processed Successfully!\n\n${message}`);
      }

      // Refresh data
      const params = {
        establishment_id: selectedEstablishment,
        month: selectedMonth,
        year: selectedYear
      };

      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      const refreshResponse = await payrollAPI.getMonthly(params);
      setPayrollData(refreshResponse.payroll || []);
      setSummary(refreshResponse.summary || null);

    } catch (error) {
      console.error('❌ Error processing payroll:', error);
      alert('Error processing payroll:\n\n' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadCSV = (rows, filename) => {
    const csvContent = rows.map(row => row.map(cell =>
      typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
    ).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportPFStatement = () => {
    if (!payrollData || payrollData.length === 0) return;
    const label = getMonthLabel().replace(' ', '_');
    const headers = ['No', 'Employee Name', 'Employee ID', 'UAN', 'Basic', 'Employee PF (12%)', 'Employer PF (13%)', 'Total PF'];
    const rows = payrollData.map((r, i) => {
      const empPF = parseFloat(r.pf || 0);
      const erPF = parseFloat(r.pf_employer || 0) || parseFloat(r.basic || 0) * 0.13;
      return [i + 1, `${r.first_name} ${r.last_name}`, r.employee_id, r.uan || '', formatNumber(r.basic), empPF.toFixed(2), erPF.toFixed(2), (empPF + erPF).toFixed(2)];
    });
    downloadCSV([headers, ...rows], `PF_Statement_${label}.csv`);
  };

  const exportESIStatement = () => {
    if (!payrollData || payrollData.length === 0) return;
    const label = getMonthLabel().replace(' ', '_');
    const headers = ['No', 'Employee Name', 'Employee ID', 'ESIC IP No', 'Gross Wages', 'ESI Contribution', 'ESI Eligible'];
    const rows = payrollData.map((r, i) => [
      i + 1, `${r.first_name} ${r.last_name}`, r.employee_id, r.esic_ip || r.esino || '',
      formatNumber(r.gross_wages), formatNumber(r.esic), parseFloat(r.esic || 0) > 0 ? 'Yes' : 'No'
    ]);
    downloadCSV([headers, ...rows], `ESI_Statement_${label}.csv`);
  };

  const exportBankTransfer = () => {
    if (!payrollData || payrollData.length === 0) return;
    const label = getMonthLabel().replace(' ', '_');
    const headers = ['No', 'Employee Name', 'Employee ID', 'Bank Account', 'IFSC Code', 'Net Payable'];
    const rows = payrollData.map((r, i) => [
      i + 1, `${r.first_name} ${r.last_name}`, r.employee_id,
      r.bank_account || r.account_number || '', r.ifsc_code || '', formatNumber(r.net_payable)
    ]);
    downloadCSV([headers, ...rows], `Bank_Transfer_${label}.csv`);
  };

  const exportToExcel = () => {
    if (!payrollData || payrollData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel export
    const excelData = payrollData.map((record, index) => {
      const otherDeductions = parseFloat(record.advance_recovery || 0) +
                             parseFloat(record.fines || 0) +
                             parseFloat(record.damage_loss || 0) +
                             parseFloat(record.other_deductions || 0);
      const employerPf = record.pf_employer || (parseFloat(record.basic || 0) * 0.13);

      return {
        'No': index + 1,
        'NAME': `${record.first_name} ${record.last_name}`,
        'EID No': record.employee_id,
        'FATHER NAME': record.father_name || '',
        'Desig': record.designation || '',
        'UAN': record.uan || '',
        'Working Days': record.working_days || 0,
        'Hourly Wage': parseFloat(record.hourly_wage || 0).toFixed(2),
        'Basic': parseFloat(record.basic || 0).toFixed(2),
        'DA': parseFloat(record.da || 0).toFixed(2),
        'HRA': parseFloat(record.hra || 0).toFixed(2),
        'Conveyance': parseFloat(record.conveyance || 0).toFixed(2),
        'Employer PF (13%)': parseFloat(employerPf).toFixed(2),
        'OT Amount': parseFloat(record.ot_amount || 0).toFixed(2),
        'Gross Wages': parseFloat(record.gross_wages || 0).toFixed(2),
        'PF Employee (12%)': parseFloat(record.pf || 0).toFixed(2),
        'PF Employer (13%)': parseFloat(employerPf).toFixed(2),
        'ESI': parseFloat(record.esic || 0).toFixed(2),
        'PT': parseFloat(record.pt || 0).toFixed(2),
        'TDS': parseFloat(record.tds || 0).toFixed(2),
        'Other Deductions': otherDeductions.toFixed(2),
        'Total Deductions': parseFloat(record.total_deductions || 0).toFixed(2),
        'Net Pay': parseFloat(record.net_payable || 0).toFixed(2)
      };
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const columnWidths = [
      { wch: 5 },   // No
      { wch: 20 },  // NAME
      { wch: 12 },  // EID No
      { wch: 20 },  // FATHER NAME
      { wch: 15 },  // Desig
      { wch: 15 },  // UAN
      { wch: 12 },  // Working Days
      { wch: 12 },  // Hourly Wage
      { wch: 12 },  // Basic
      { wch: 12 },  // DA
      { wch: 12 },  // HRA
      { wch: 12 },  // Conveyance
      { wch: 15 },  // Employer PF
      { wch: 12 },  // OT Amount
      { wch: 12 },  // Gross Wages
      { wch: 15 },  // PF Employee
      { wch: 15 },  // PF Employer
      { wch: 12 },  // ESI
      { wch: 12 },  // PT
      { wch: 12 },  // TDS
      { wch: 15 },  // Other Deductions
      { wch: 15 },  // Total Deductions
      { wch: 12 }   // Net Pay
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll');

    // Generate Excel file
    const fileName = `Payroll_${getMonthLabel().replace(' ', '_')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="update-employee-container">
      <div className="update-employee-wrapper">
        <div className="update-employee-header">
          <h2 className="update-employee-title">Payroll Processing</h2>
          <p className="update-employee-subtitle">Process and manage monthly payroll for employees</p>
        </div>

        {/* Selection Filters */}
        <div className="attendance-grid-filters">
          {/* Organization Dropdown — hidden for ClientAdmin */}
          {!lockedEstId && (
          <div className="attendance-filter-group">
            <label className="attendance-filter-label">
              Organization
            </label>
            <div style={{ position: 'relative', width: '100%', minWidth: '300px' }}>
              <select
                value={selectedEstablishment}
                onChange={handleEstablishmentChange}
                className="attendance-filter-select"
                style={{ appearance: 'none', paddingRight: '2rem', width: '100%' }}
                disabled={loading}
              >
                <option value="">
                  {loading ? 'Loading...' : 'Select Organization'}
                </option>
                {establishments.map((est) => (
                  <option key={est.id} value={est.id}>
                    {est.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#6b7280'
              }} />
            </div>
          </div>
          )}

          {/* Month Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper month">
                  <Calendar size={15} />
                </span>
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="attendance-filter-select"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Year Dropdown */}
          {selectedEstablishment && (
            <div className="attendance-filter-group animate-fadeIn">
              <label className="attendance-filter-label">
                <span className="filter-icon-wrapper month">
                  <Calendar size={15} />
                </span>
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="attendance-filter-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Process Payroll Section */}
        {selectedEstablishment && (
          <div className="update-employee-form animate-fadeIn" style={{ marginTop: '1.5rem' }}>
            <div className="update-employee-content">
              {/* Process Payroll Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #a5b4fc 0%, #c084fc 100%)',
                borderRadius: '0.75rem',
                color: 'white'
              }}>
                <div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, marginBottom: '0.5rem' }}>
                    Process Payroll
                  </h3>
                  <p style={{ fontSize: '0.875rem', margin: 0, opacity: 0.9 }}>
                    {getMonthLabel()}
                  </p>
                </div>
                <button
                  onClick={handleGeneratePayroll}
                  disabled={isGenerating || !selectedEstablishment}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: isGenerating ? 'rgba(255,255,255,0.3)' : '#ffffff',
                    color: isGenerating ? 'white' : '#7c6fc4',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: isGenerating || !selectedEstablishment ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    opacity: isGenerating || !selectedEstablishment ? 0.6 : 1
                  }}
                >
                  {isGenerating ? (
                    <>
                      <div className="update-employee-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Process Payroll
                    </>
                  )}
                </button>
              </div>

              {/* Loading/Processing/No Data States */}
              {(dataLoading || isGenerating) && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  <div className="update-employee-spinner" style={{ margin: '0 auto 1.5rem', width: '48px', height: '48px' }}></div>
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    {isGenerating ? 'Processing Payroll...' : 'Loading payroll data...'}
                  </p>
                  <p style={{ fontSize: '0.875rem' }}>
                    {isGenerating ? 'Calculating payroll for all employees. This may take a few moments.' : 'Please wait...'}
                  </p>
                </div>
              )}

              {!dataLoading && !isGenerating && (!payrollData || payrollData.length === 0) && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6b7280',
                  background: 'var(--theme-surfaceHover)',
                  borderRadius: '0.5rem',
                  border: '2px dashed #d1d5db'
                }}>
                  <FileText size={48} style={{ color: '#d1d5db', margin: '0 auto 1rem' }} />
                  <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    No payroll data found for {getMonthLabel()}
                  </p>
                  <p style={{ fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    Click the "Process Payroll" button above to calculate and save payroll data for this month.
                  </p>
                </div>
              )}

              {/* Payroll Processing Results */}
              {!dataLoading && !isGenerating && payrollData && payrollData.length > 0 && (
                <>
                  {/* Success Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '0.75rem',
                    padding: '2rem',
                    marginBottom: '2rem',
                    color: 'white',
                    boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <CheckCircle size={40} style={{ flexShrink: 0 }} />
                      <div>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', margin: 0, marginBottom: '0.25rem' }}>
                          Payroll Processing Complete
                        </h3>
                        <p style={{ fontSize: '0.875rem', margin: 0, opacity: 0.9 }}>
                          Successfully processed payroll for {getMonthLabel()}
                        </p>
                      </div>
                    </div>

                    {/* Summary Statistics */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem',
                      marginTop: '1.5rem'
                    }}>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Total Employees</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>{payrollData.length}</div>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Total Gross Salary</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                          ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.gross_wages || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Total Deductions</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                          ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.total_deductions || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.25)',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>Net Payable</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                          ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.net_payable || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payroll Statements Section */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: '#111827' }}>
                        Payroll Statements
                      </h3>
                    </div>

                    {/* Statement Cards Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
                      {/* PF Statement Card */}
                      <div style={{
                        background: 'var(--theme-surface)',
                        border: '2px solid #10b98130',
                        borderRadius: '0.75rem',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#10b98110',
                          padding: '1rem',
                          borderBottom: '1px solid #10b98130'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                background: '#10b981',
                                padding: '0.5rem',
                                borderRadius: '0.5rem',
                                display: 'flex'
                              }}>
                                <Users size={20} color="white" />
                              </div>
                              <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#10b981' }}>
                                  PF Statement
                                </h4>
                                <p style={{ fontSize: '0.75rem', margin: 0, color: '#6b7280' }}>
                                  {payrollData.length} employees processed
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={exportPFStatement}
                              title="Download PF Statement CSV"
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.375rem 0.75rem', background: '#10b981',
                                color: 'white', border: 'none', borderRadius: '0.375rem',
                                cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'
                              }}
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </div>
                        </div>
                        <div style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Employee PF (12%)</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.pf || 0), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Employer PF (13%)</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              ₹{payrollData.reduce((sum, r) => sum + (parseFloat(r.pf_employer || 0) || parseFloat(r.basic || 0) * 0.13), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div style={{
                            marginTop: '1rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>Total PF</span>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#10b981' }}>
                              ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.pf || 0) + (parseFloat(r.pf_employer || 0) || parseFloat(r.basic || 0) * 0.13), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* ESI Statement Card */}
                      <div style={{
                        background: 'var(--theme-surface)',
                        border: '2px solid #3b82f630',
                        borderRadius: '0.75rem',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#3b82f610',
                          padding: '1rem',
                          borderBottom: '1px solid #3b82f630'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                background: '#3b82f6',
                                padding: '0.5rem',
                                borderRadius: '0.5rem',
                                display: 'flex'
                              }}>
                                <TrendingUp size={20} color="white" />
                              </div>
                              <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#3b82f6' }}>
                                  ESI Statement
                                </h4>
                                <p style={{ fontSize: '0.75rem', margin: 0, color: '#6b7280' }}>
                                  Employee State Insurance
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={exportESIStatement}
                              title="Download ESI Statement CSV"
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.375rem 0.75rem', background: '#3b82f6',
                                color: 'white', border: 'none', borderRadius: '0.375rem',
                                cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'
                              }}
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </div>
                        </div>
                        <div style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Gross Wages</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.gross_wages || 0), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Eligible Employees</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              {payrollData.filter(r => parseFloat(r.esic || 0) > 0).length}
                            </span>
                          </div>
                          <div style={{
                            marginTop: '1rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>Total ESI</span>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#3b82f6' }}>
                              ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.esic || 0), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Bank Transfer Card */}
                      <div style={{
                        background: 'var(--theme-surface)',
                        border: '2px solid #8b5cf630',
                        borderRadius: '0.75rem',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: '#8b5cf610',
                          padding: '1rem',
                          borderBottom: '1px solid #8b5cf630'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                background: '#8b5cf6',
                                padding: '0.5rem',
                                borderRadius: '0.5rem',
                                display: 'flex'
                              }}>
                                <FileText size={20} color="white" />
                              </div>
                              <div>
                                <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0, color: '#8b5cf6' }}>
                                  Bank Transfer
                                </h4>
                                <p style={{ fontSize: '0.75rem', margin: 0, color: '#6b7280' }}>
                                  Salary disbursement summary
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={exportBankTransfer}
                              title="Download Bank Transfer CSV"
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.375rem 0.75rem', background: '#8b5cf6',
                                color: 'white', border: 'none', borderRadius: '0.375rem',
                                cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'
                              }}
                            >
                              <Download size={14} />
                              Download
                            </button>
                          </div>
                        </div>
                        <div style={{ padding: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Employees to be paid</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              {payrollData.length}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Average Salary</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                              ₹{(payrollData.reduce((sum, r) => sum + parseFloat(r.net_payable || 0), 0) / payrollData.length).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div style={{
                            marginTop: '1rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>Total Payout</span>
                            <span style={{ fontSize: '1rem', fontWeight: '700', color: '#8b5cf6' }}>
                              ₹{payrollData.reduce((sum, r) => sum + parseFloat(r.net_payable || 0), 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Pay Statement with Scroll */}
                  <div style={{ marginTop: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        margin: 0,
                        color: '#111827'
                      }}>
                        Detailed Pay Statement
                      </h3>
                      <button
                        onClick={exportToExcel}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.625rem 1.25rem',
                          background: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)'
                        }}
                      >
                        <Download size={16} />
                        Export to Excel
                      </button>
                    </div>
                    <div style={{
                      background: 'var(--theme-surface)',
                      borderRadius: '0.75rem',
                      border: '1px solid #e5e7eb',
                      overflow: 'auto',
                      maxHeight: '500px'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--theme-surfaceHover)', zIndex: 2 }}>
                          {/* First header row - Main groups */}
                          <tr>
                            <th rowSpan={2} style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', fontSize: '0.75rem', color: '#374151', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb', minWidth: '120px' }}>Employee</th>
                            <th rowSpan={2} style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', fontSize: '0.75rem', color: '#374151', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb', minWidth: '80px' }}>ID</th>
                            <th colSpan={2} style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.75rem', color: '#374151', background: '#fef3c7', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Days & Wages</th>
                            <th colSpan={7} style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.75rem', color: '#374151', background: '#d1fae5', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Earnings</th>
                            <th colSpan={7} style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.75rem', color: '#374151', background: '#fecaca', borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Deductions</th>
                            <th rowSpan={2} style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', fontSize: '0.75rem', color: '#059669', borderBottom: '2px solid #e5e7eb', minWidth: '100px' }}>Net Pay</th>
                          </tr>
                          {/* Second header row - Sub columns */}
                          <tr>
                            {/* Days & Wages */}
                            <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: '600', fontSize: '0.7rem', color: '#92400e', background: '#fef3c7', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Work<br/>Days</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#92400e', background: '#fef3c7', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Hourly<br/>Wage</th>
                            {/* Earnings */}
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Basic</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>DA</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>HRA</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Convey</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Emp<br/>PF</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>OT</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#065f46', background: '#d1fae5', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Gross</th>
                            {/* Deductions */}
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>PF<br/>Emp</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>PF<br/>Empr</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>ESI</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>PT</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>TDS</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Other</th>
                            <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600', fontSize: '0.7rem', color: '#991b1b', background: '#fecaca', borderBottom: '2px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>Total<br/>Ded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payrollData.map((record, index) => (
                            <tr key={record.wage_id} style={{
                              borderBottom: '1px solid #f8f9fa',
                              background: index % 2 === 0 ? '#ffffff' : '#f1f3f4'
                            }}>
                              {/* Employee Info */}
                              <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#111827', fontWeight: '500', borderRight: '1px solid #e5e7eb' }}>
                                {record.first_name} {record.last_name}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
                                {record.employee_id}
                              </td>
                              {/* Days & Wages */}
                              <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: '#92400e', fontWeight: '600', background: '#fffbeb', borderRight: '1px solid #e5e7eb', title: 'Working Days = Total Days - Week Offs - LOP' }}>
                                {record.working_days || 0}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#92400e', background: '#fffbeb', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.hourly_wage || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              {/* Earnings */}
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.basic || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.da || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.hra || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.conveyance || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb', title: 'Employer PF Contribution (13%)' }}>
                                ₹{parseFloat(record.pf_employer || (parseFloat(record.basic || 0) * 0.13)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#065f46', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.ot_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', color: '#065f46', fontWeight: '600', background: '#ecfdf5', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.gross_wages || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              {/* Deductions */}
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb', title: 'Employee PF (12%)' }}>
                                ₹{parseFloat(record.pf || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb', title: 'Employer PF (13%)' }}>
                                ₹{parseFloat(record.pf_employer || (parseFloat(record.basic || 0) * 0.13)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.esic || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.pt || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.tds || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.7rem', color: '#991b1b', background: '#fef2f2', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat((record.advance_recovery || 0) + (record.fines || 0) + (record.damage_loss || 0) + (record.other_deductions || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.75rem', color: '#991b1b', fontWeight: '600', background: '#fef2f2', borderRight: '1px solid #e5e7eb' }}>
                                ₹{parseFloat(record.total_deductions || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                              {/* Net Pay */}
                              <td style={{ padding: '0.5rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: '700', color: '#059669' }}>
                                ₹{parseFloat(record.net_payable || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyPayrollRegistry;
